import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConditionSetEditor } from '@/components/conditions/ConditionSetEditor';
import type { ConditionCatalog, ConditionSetsResponse } from '@/components/conditions/types';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/settings/work-record-approvers` (v0.4 追加要件⑳-3、D-581〜D-596)。
 * 勤務実績 (work_records) の承認者をテナントが設定できるようにする。
 * ⚠️ security-reviewer 事前レビュー H-1/H-2 (実装着手前相談、CLAUDE.md §6b-2):
 * 承認権限の付与は職務分掌に直結する機微な設定のため、書込み (作成・解除) は node_id の
 * 有無を問わず全店舗スコープ (tenant_config:manage の all_stores) 保有者のみに許可する
 * (own_store/node_scope の manage 保有者には閲覧のみ許可し、ボタンを出さない)。
 * 設定しない場合は「対象店舗で shift:manage を持つ人なら誰でも承認できる」という既定の
 * 挙動のまま (D-589、後方互換)。
 * 設計: docs/design/v04-planned-vs-actual-separation-detail.md §4g
 */

const WORK_RECORD_APPROVER_USAGE_POINT_ID = 'work_record_approver';
const TENANT_DEFAULT = '__tenant_default__';

type Assignment = {
  assignment_id: string;
  node_id: string | null;
  resolution_mode: 'explicit_employee' | 'condition_set';
  assignee_employee_id: string | null;
  condition_set_id: string | null;
  label: string | null;
  created_at: string;
};
type Employee = { employee_id: string; employee_number: string; family_name: string; given_name: string };
type PreviewResult = { matched_count: number; matched_persons: Array<{ employee_id: string; display_name: string }> };

function NodeSelector({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  return (
    <div className="min-w-0 w-full sm:w-72">
      <label className="block text-xs text-text-sub mb-1">対象ノード</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={TENANT_DEFAULT}>テナント全体 (既定)</SelectItem>
          {flatNodes.map(({ node, depth }) => (
            <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

type AssignmentForm = {
  resolutionMode: 'explicit_employee' | 'condition_set';
  employeeId: string;
  conditionSetId: string;
  label: string;
};

export function WorkRecordApproversSettingsPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const permsReady = usePermsReady();
  const canView = hasPermission(user?.effective_permissions, 'tenant_config', 'view');
  // H-1/H-2: 書込みは常に all_stores 限定 (node_id の有無を問わない)。
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'tenant_config', 'manage');

  const [selectedNode, setSelectedNode] = useState(TENANT_DEFAULT);
  const nodeIdParam = selectedNode === TENANT_DEFAULT ? null : selectedNode;
  const [form, setForm] = useState<AssignmentForm | null>(null);
  const [showConditionEditor, setShowConditionEditor] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);

  // ⚠️ バックエンド (routes/work-records.ts GET) は node_id='' (テナント全体) も all_stores 限定と
  // 同格に扱う (テナント全体既定はどの店舗にも波及する機微な設定のため own_store/node_scope の
  // 閲覧者には見せない、H-1と同じ判断を読み取りにも適用)。own_store/node_scope の viewer が
  // 「テナント全体」を選んだ場合は 403 になりうるため isError を明示的に扱う (§8-76 と同じ理由で
  // 早期returnにはしない、hooks は全て先に呼び終える)。
  const { data: assignments = [], isError: assignmentsError } = useQuery<Assignment[]>({
    queryKey: ['work-record-approver-assignments', nodeIdParam],
    queryFn: () => apiFetch<Assignment[]>(`/work-records/approver-assignments?node_id=${nodeIdParam ?? ''}`),
    enabled: canView, retry: false,
  });
  // ⚠️ Testレビュー指摘反映 (ApprovalsPage.tsx と同型): GET /employees は isCompanyAdmin ロール判定
  // のため、tenant_config:manage を持つが company_admin ロールではないカスタムロールでは
  // 403 になりうる。isError を無視すると社員選択の Select が無言で空になるため明示的に扱う。
  const { data: employees = [], isError: employeesError } = useQuery<Employee[]>({
    queryKey: ['employees'], queryFn: () => apiFetch<Employee[]>('/employees'), enabled: !!form, retry: false,
  });
  const { data: conditionCatalog } = useQuery<ConditionCatalog>({
    queryKey: ['condition-catalog'], queryFn: () => apiFetch<ConditionCatalog>('/condition-catalog'),
    staleTime: 5 * 60 * 1000,
  });
  const { data: conditionSetsData } = useQuery<ConditionSetsResponse>({
    queryKey: ['condition-sets', WORK_RECORD_APPROVER_USAGE_POINT_ID],
    queryFn: () => apiFetch<ConditionSetsResponse>(`/condition-sets?usage_point_id=${WORK_RECORD_APPROVER_USAGE_POINT_ID}`),
  });
  const conditionSets = conditionSetsData?.sets ?? [];
  const attributeTypesById = new Map((conditionCatalog?.attribute_types ?? []).map((t) => [t.attribute_type_id, t]));
  const usagePoint = conditionCatalog?.usage_points.find((u) => u.usage_point_id === WORK_RECORD_APPROVER_USAGE_POINT_ID);

  const employeeName = (id: string) => {
    const e = employees.find((x) => x.employee_id === id);
    return e ? `${e.family_name} ${e.given_name}` : id;
  };

  const createMut = useMutation({
    mutationFn: (f: AssignmentForm) => apiFetch('/work-records/approver-assignments', {
      method: 'POST',
      body: JSON.stringify({
        node_id: nodeIdParam, resolution_mode: f.resolutionMode,
        ...(f.resolutionMode === 'explicit_employee' ? { assignee_employee_id: f.employeeId } : { condition_set_id: f.conditionSetId }),
        label: f.label.trim() || undefined,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-record-approver-assignments', nodeIdParam] });
      setForm(null);
      toast.success('承認者設定を登録しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '登録に失敗しました'),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/work-records/approver-assignments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-record-approver-assignments', nodeIdParam] });
      toast.success('承認者設定を解除しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '解除に失敗しました'),
  });

  const previewMut = useMutation({
    mutationFn: (conditionSetId: string) => apiFetch<PreviewResult>('/work-records/approver-assignments/preview-condition', {
      method: 'POST', body: JSON.stringify({ condition_set_id: conditionSetId }),
    }),
    onSuccess: (res) => setPreviewResult(res),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'プレビューに失敗しました'),
  });

  if (!permsReady) return <div role="status" className="text-text-sub">読み込み中…</div>;
  if (!canView) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold text-text-body">勤務実績の承認者</h2>
        <p className="text-sm text-text-sub mt-1">
          勤務実績 (誰が働いたかの実績記録) を承認できる人を設定します。設定しない場合は
          「対象店舗で「シフト管理」の管理権限を持つ人なら誰でも承認できる」という既定の挙動のままです。
        </p>
      </div>

      <NodeSelector value={selectedNode} onChange={setSelectedNode} />

      <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-bold text-text-body">
            {selectedNode === TENANT_DEFAULT ? 'テナント全体の承認者設定' : 'このノードの承認者設定'}
          </h3>
          {canManage && (
            <Button
              variant="secondary" size="sm" leftIcon={<Plus size={14} />}
              onClick={() => setForm({ resolutionMode: 'explicit_employee', employeeId: '', conditionSetId: '', label: '' })}
            >
              承認者を追加
            </Button>
          )}
        </div>
        {!canManage && <p className="text-xs text-text-sub">閲覧のみ可能です (編集にはテナント全体の管理権限が必要です)。</p>}

        {assignmentsError ? (
          <p className="text-xs text-alert-text">
            この範囲の設定を閲覧するには全店舗の権限が必要です。特定の店舗を選び直してください。
          </p>
        ) : assignments.length === 0 ? (
          <EmptyState
            message="承認者の設定はありません"
            description="対象店舗で「シフト管理」の管理権限を持つ人なら誰でも承認できる、既定の挙動のままです。"
          />
        ) : (
          <div className="divide-y divide-surface-border">
            {assignments.map((a) => (
              <div key={a.assignment_id} className="flex items-center justify-between gap-2 py-2 text-sm">
                <div>
                  <span className="font-medium text-text-body">
                    {a.label || (a.resolution_mode === 'explicit_employee' && a.assignee_employee_id ? employeeName(a.assignee_employee_id) : '条件セットで指定')}
                  </span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-surface-sunken text-text-body ml-2">
                    {a.resolution_mode === 'explicit_employee' ? '個人指名' : '条件による指定'}
                  </span>
                </div>
                {canManage && (
                  <button
                    type="button" aria-label="承認者設定を解除" onClick={() => revokeMut.mutate(a.assignment_id)}
                    className="p-1.5 rounded-md text-text-sub hover:text-danger-text hover:bg-surface-sunken min-h-[44px] min-w-[44px] flex items-center justify-center"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {canManage && (
          <div>
            <button
              type="button" onClick={() => setShowConditionEditor((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-text-sub hover:text-text-body min-h-[44px]"
            >
              {showConditionEditor ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              対象者条件 (承認者向け) を管理
            </button>
            {showConditionEditor && usagePoint && (
              <div className="mt-2">
                <ConditionSetEditor usagePointId={WORK_RECORD_APPROVER_USAGE_POINT_ID} usagePoint={usagePoint} attributeTypesById={attributeTypesById} />
              </div>
            )}
          </div>
        )}
      </div>

      {form && (
        <Modal
          open title="承認者を追加" onClose={() => setForm(null)} size="sm"
          footer={(
            <>
              <Button variant="secondary" onClick={() => setForm(null)}>キャンセル</Button>
              <Button
                variant="primary"
                disabled={createMut.isPending || (form.resolutionMode === 'explicit_employee' ? !form.employeeId : !form.conditionSetId)}
                loading={createMut.isPending}
                onClick={() => createMut.mutate(form)}
              >
                登録
              </Button>
            </>
          )}
        >
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-text-sub mb-1">指定方法</label>
              <Select value={form.resolutionMode} onValueChange={(v) => setForm({ ...form, resolutionMode: v as AssignmentForm['resolutionMode'] })}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="explicit_employee">特定の1名を指名する</SelectItem>
                  <SelectItem value="condition_set">条件に一致する人を対象とする</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.resolutionMode === 'explicit_employee' ? (
              <div>
                <label className="block text-xs text-text-sub mb-1">承認者に指名する社員</label>
                {employeesError && (
                  <p className="text-xs text-alert-text mb-1">社員一覧の取得に失敗しました (社員管理の閲覧権限が必要です)。</p>
                )}
                <Select value={form.employeeId || undefined} onValueChange={(v) => setForm({ ...form, employeeId: v })}>
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="社員を選ぶ" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.family_name} {e.given_name} ({e.employee_number})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <label className="block text-xs text-text-sub mb-1">対象者条件</label>
                <Select value={form.conditionSetId || undefined} onValueChange={(v) => setForm({ ...form, conditionSetId: v })}>
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="条件セットを選ぶ" /></SelectTrigger>
                  <SelectContent>
                    {conditionSets.map((s) => <SelectItem key={s.set_id} value={s.set_id}>{s.label || s.set_id}</SelectItem>)}
                  </SelectContent>
                </Select>
                {conditionSets.length === 0 && (
                  <p className="text-xs text-text-sub mt-1">
                    まだ条件セットがありません。上の「対象者条件を管理」から先に作成してください。
                  </p>
                )}
                {form.conditionSetId && (
                  <button
                    type="button"
                    className="mt-2 inline-flex items-center gap-1 text-xs text-text-sub hover:text-text-body min-h-[44px]"
                    disabled={previewMut.isPending}
                    onClick={() => previewMut.mutate(form.conditionSetId)}
                  >
                    <Search size={14} /> この条件で何人が対象になるか確認する
                  </button>
                )}
              </div>
            )}
            <div>
              <label className="block text-xs text-text-sub mb-1">表示名 (任意)</label>
              <input
                type="text" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                maxLength={200}
                className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring"
              />
            </div>
          </div>
        </Modal>
      )}

      {previewResult && (
        <Modal open title="対象者プレビュー" onClose={() => setPreviewResult(null)} size="sm" footer={<Button variant="primary" onClick={() => setPreviewResult(null)}>閉じる</Button>}>
          <p className="text-sm text-text-body mb-2">現在この条件に一致する人数: {previewResult.matched_count}名</p>
          {previewResult.matched_persons.length > 0 ? (
            <ul className="text-sm text-text-body list-disc list-inside space-y-0.5">
              {previewResult.matched_persons.map((p) => <li key={p.employee_id}>{p.display_name}</li>)}
            </ul>
          ) : (
            previewResult.matched_count > 0 && (
              <p className="text-xs text-text-sub">氏名の一覧を見るには全店舗の閲覧権限が必要です。</p>
            )
          )}
        </Modal>
      )}
    </div>
  );
}
